/* --- EXTERNAL --- */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

/* --- REDUX --- */
import { selectedLang } from './app/client-redux/languages';
import { AppDispatch } from './app/client-redux/store';
import { AppState } from './app/client-redux/reducers';

/* --- COMPONENTS & UTILS --- */
import { WithNavBar } from './app/components/nav-bar/with-nav-bar';
import { navigatorRef } from './app/utils/navigate-script';

/* --- PAGES --- */
/* --- APM --- */
import APMEquipments from './app/pages/apm/equipments';
import APMPeriods from './app/pages/apm/periods';
import APMTransactions from './app/pages/apm/transactions';

/* --- Dashboards --- */
import Dashboard from './app/pages/dashboardV2';
import DashboardEntity from './app/pages/dashboard-entity';
import DashboardV2 from './app/pages/dashboardV2';
import EquipmentDashboard from './app/pages/dashboardV2/equipmentDashboard';

/* --- Helpdesk --- */
import Contract from './app/helpdesk-app/pages/contract';
import HelpdeskAuditLogs from './app/helpdesk-app/pages/audit-logs';
import HelpdeskContracts from './app/helpdesk-app/pages/contracts-list';
import HelpdeskDevices from './app/helpdesk-app/pages/devices';
import HelpdeskEntities from './app/helpdesk-app/pages/entities';
import HelpdeskEntity from './app/helpdesk-app/pages/entity';
import HelpdeskEquipment from './app/helpdesk-app/pages/equipments';
import RegisterEquipmentHelpdesk from './app/helpdesk-app/pages/equipments/register-equipment';
import HelpdeskEventManagement from './app/helpdesk-app/pages/event-management-list';
import HelpdeskEvents from './app/helpdesk-app/pages/events';
import HelpdeskLocation from './app/helpdesk-app/pages/location';
import HelpdeskLocationList from './app/helpdesk-app/pages/locations-list';
import HelpdeskProductManagement from './app/helpdesk-app/pages/product-management';
import HelpDeskSyncCommands from './app/helpdesk-app/pages/sync-commands';
import HelpdeskTicketList from './app/helpdesk-app/pages/ticket-list';
import Ticket from './app/helpdesk-app/pages/ticket';

/* --- Main pages --- */
import BankTransactions from './app/pages/bank-transactions';
import Distribuition from './app/pages/distribution';
import DistributionsList from './app/pages/distributions';
import Equipment from './app/pages/equipments';
import EquipmentConfigForm from './app/pages/equipment-config';
import EquipmentCurrency from './app/pages/equipment-currency';
import Locations from './app/pages/locations';
import { Login } from './app/pages/login';
import PeriodsV2 from './app/pages/periods';
import Profile from './app/pages/profile';
import ReasonsDeclarations from './app/pages/edit-reasons-declarations';
import Transactions from './app/pages/transactions';
import Users from './app/pages/users';
import CashCollections from './app/pages/cash-collections';
import Products from './app/pages/products';
import Dispensers from './app/pages/dispensers';
import UserSummaries from './app/pages/user-summaries';
import WithdrawSetList from './app/pages/withdraw-set-list';

/* --- SNS Sonho --- */
import UserShifts from './app/pages/user-shifts';

/* --- STYLES --- */
import './app.scss';

const App = () => {
  const token = useSelector((state: AppState) => state.login.token);
  const lang = useSelector((state: AppState) => state.lang).lang;
  const dispatch = useDispatch<AppDispatch>();
  const navigateHook = useNavigate();

  useEffect(() => {
    navigatorRef.current = navigateHook;

    return () => {
      navigatorRef.current = null;
    };
  }, [navigateHook]);

  useEffect(() => {
    if (!lang) {
      dispatch(selectedLang(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const protectedRoute = (element: JSX.Element) => {
    return token ? element : <Navigate to="/" />;
  };

  return (
    <Routes>
      {/* ===================== AUTH ===================== */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />

      {/* ===================== DASHBOARDS ===================== */}
      <Route
        path="/dashboard"
        element={protectedRoute(
          <WithNavBar>
            <Dashboard />
          </WithNavBar>
        )}
      />

      <Route
        path="/dashboardEntity"
        element={protectedRoute(
          <WithNavBar>
            <DashboardEntity />
          </WithNavBar>
        )}
      />

      <Route
        path="/dashboardV2"
        element={protectedRoute(
          <WithNavBar>
            <DashboardV2 />
          </WithNavBar>
        )}
      />

      <Route
        path="/dashboardV2/equipment/:eid"
        element={protectedRoute(
          <WithNavBar>
            <EquipmentDashboard />
          </WithNavBar>
        )}
      />

      {/* ===================== MAIN PAGES ===================== */}
      <Route
        path="/bankTransactions"
        element={protectedRoute(
          <WithNavBar>
            <BankTransactions />
          </WithNavBar>
        )}
      />

      <Route
        path="/distribution/:mode/:id"
        element={protectedRoute(
          <WithNavBar>
            <Distribuition />
          </WithNavBar>
        )}
      />

      <Route
        path="/distributions"
        element={protectedRoute(
          <WithNavBar>
            <DistributionsList />
          </WithNavBar>
        )}
      />

      <Route
        path="/equipment/currency/:mode/:eid/:sid"
        element={protectedRoute(
          <WithNavBar>
            <EquipmentCurrency />
          </WithNavBar>
        )}
      />

      <Route
        path="/equipmentConfig/:eid"
        element={protectedRoute(
          <WithNavBar>
            <EquipmentConfigForm />
          </WithNavBar>
        )}
      />

      <Route
        path="/equipments"
        element={protectedRoute(
          <WithNavBar>
            <Equipment />
          </WithNavBar>
        )}
      />

      <Route
        path="/locals"
        element={protectedRoute(
          <WithNavBar>
            <Locations />
          </WithNavBar>
        )}
      />

      <Route
        path="/periods"
        element={protectedRoute(
          <WithNavBar>
            <PeriodsV2 />
          </WithNavBar>
        )}
      />

      <Route
        path="/profile"
        element={protectedRoute(
          <WithNavBar>
            <Profile />
          </WithNavBar>
        )}
      />

      <Route
        path="/reasonsDeclarations"
        element={protectedRoute(
          <WithNavBar>
            <ReasonsDeclarations />
          </WithNavBar>
        )}
      />

      <Route
        path="/transactions"
        element={protectedRoute(
          <WithNavBar>
            <Transactions />
          </WithNavBar>
        )}
      />

      <Route
        path="/transactions/:entid/:uid"
        element={protectedRoute(
          <WithNavBar>
            <Transactions />
          </WithNavBar>
        )}
      />

      <Route
        path="/products"
        element={protectedRoute(
          <WithNavBar>
            <Products />
          </WithNavBar>
        )}
      />

      <Route
        path="/dispensers"
        element={protectedRoute(
          <WithNavBar>
            <Dispensers />
          </WithNavBar>
        )}
      />

      <Route
        path="/dispenser/:eid"
        element={protectedRoute(
          <WithNavBar>
            <Dispensers />
          </WithNavBar>
        )}
      />

      <Route
        path="/users"
        element={protectedRoute(
          <WithNavBar>
            <Users />
          </WithNavBar>
        )}
      />

      {/* ===================== APM ===================== */}
      <Route
        path="/apm/equipments"
        element={protectedRoute(
          <WithNavBar>
            <APMEquipments />
          </WithNavBar>
        )}
      />

      <Route
        path="/apm/periods"
        element={protectedRoute(
          <WithNavBar>
            <APMPeriods />
          </WithNavBar>
        )}
      />

      <Route
        path="/apm/transactions"
        element={protectedRoute(
          <WithNavBar>
            <APMTransactions />
          </WithNavBar>
        )}
      />

      <Route
        path="/userShifts"
        element={protectedRoute(
          <WithNavBar>
            <UserShifts />
          </WithNavBar>
        )}
      />

      <Route
        path="/cashCollections"
        element={protectedRoute(
          <WithNavBar>
            <CashCollections />
          </WithNavBar>
        )}
      />

      {/* ===================== HELPDESK ===================== */}
      <Route
        path="/helpdesk/auditLogs"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskAuditLogs />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/auditLogs/:eid"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskAuditLogs />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/contract/:mode/:contractID"
        element={protectedRoute(
          <WithNavBar>
            <Contract />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/contracts"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskContracts />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/devices"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskDevices />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/entities"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEntities />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/entity/:mode/:entid"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEntity />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/equipment/registration/:eid"
        element={protectedRoute(
          <WithNavBar>
            <RegisterEquipmentHelpdesk />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/equipments"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEquipment />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/eventManagement"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEventManagement />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/events"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEvents />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/events/:eid"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskEvents />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/location/:mode/:entid/:sid"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskLocation />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/locations"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskLocationList />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/productsManagement"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskProductManagement />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/syncCommands"
        element={protectedRoute(
          <WithNavBar>
            <HelpDeskSyncCommands />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/ticket/:mode/"
        element={protectedRoute(
          <WithNavBar>
            <Ticket />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/ticket/:mode/:ticketID"
        element={protectedRoute(
          <WithNavBar>
            <Ticket />
          </WithNavBar>
        )}
      />

      <Route
        path="/helpdesk/tickets"
        element={protectedRoute(
          <WithNavBar>
            <HelpdeskTicketList />
          </WithNavBar>
        )}
      />

      <Route
        path="/userSummariesList"
        element={protectedRoute(
          <WithNavBar>
            <UserSummaries />
          </WithNavBar>
        )}
      />

      <Route
        path="/withdrawSetList"
        element={protectedRoute(
          <WithNavBar>
            <WithdrawSetList />
          </WithNavBar>
        )}
      />
    </Routes>
  );
};

export default App;
